import type { ScormData } from './commit-payload'
import type { ScormInteractionData } from './interactions'
import { commitPayload } from './commit-payload'
import { iso8601DurationToMs } from './durations'
import { interactionEntries, interactionIndexOf, readInteraction } from './interactions'
import { ErrorCode, SCORM2004_ERROR_STRINGS } from './types'

type OnCommitCallback = (data: ScormData) => Promise<void>

/** Notifié quand le contenu appelle Terminate/LMSFinish (fin de session côté module). */
type OnTerminateCallback = (info: { completed: boolean }) => void

export class ScormApiBridge {
  private data: Record<string, string> = {}
  private initialized = false
  private terminated = false
  private lastError: ErrorCode = ErrorCode.NO_ERROR
  private onCommit: OnCommitCallback
  private onTerminate?: OnTerminateCallback
  private initialTotalTimeMs: number
  // Vrai si le contenu a posé cmi.success_status lui-même pendant cette
  // session. La valeur seedée depuis la DB (resume) reste lisible via GetValue
  // mais n'est pas renvoyée au serveur : elle peut avoir été dérivée du score,
  // la réémettre la figerait comme si le module l'avait posée.
  private successSetByContent = false
  private commitQueue: Promise<void>[] = []
  // Positions des interactions modifiées depuis le dernier commit (#158) : seul
  // ce delta part au serveur, pas tout l'historique de la tentative à chaque fois.
  private changedInteractions = new Set<number>()
  // Format des réponses envoyées : 2004 tel quel ; l'adapter 1.2 y branche sa traduction.
  private formatInteraction = (interaction: ScormInteractionData) => interaction
  // Commits en attente du précédent : ils partent l'un après l'autre, jamais en
  // parallèle — deux sauvegardes simultanées se disputeraient les mêmes lignes
  // côté serveur, et la plus ancienne pourrait arriver en dernier.
  private waitingCommits: (() => void)[] = []
  private sending = false

  constructor(initialData: ScormData | null, onCommit: OnCommitCallback, onTerminate?: OnTerminateCallback) {
    this.onCommit = onCommit
    this.onTerminate = onTerminate
    this.initialTotalTimeMs = 0

    if (initialData) {
      if (initialData.completionStatus) this.data['cmi.completion_status'] = initialData.completionStatus.toLowerCase().replace('_', ' ')
      if (initialData.successStatus) this.data['cmi.success_status'] = initialData.successStatus.toLowerCase()
      if (initialData.scoreRaw != null) this.data['cmi.score.raw'] = String(initialData.scoreRaw)
      if (initialData.scoreMin != null) this.data['cmi.score.min'] = String(initialData.scoreMin)
      if (initialData.scoreMax != null) this.data['cmi.score.max'] = String(initialData.scoreMax)
      if (initialData.scoreScaled != null) this.data['cmi.score.scaled'] = String(initialData.scoreScaled)
      if (initialData.totalTime) {
        this.data['cmi.total_time'] = initialData.totalTime
        this.initialTotalTimeMs = iso8601DurationToMs(initialData.totalTime)
      }
      if (initialData.suspendData) this.data['cmi.suspend_data'] = initialData.suspendData
      if (initialData.location) this.data['cmi.location'] = initialData.location
      // Les réponses de la tentative reprise (#158) : la norme veut qu'elles
      // persistent, et un module qui ajoute à `_count` écraserait sinon les
      // premières. Déjà enregistrées, elles ne repartent pas au serveur.
      for (const interaction of initialData.interactions ?? []) Object.assign(this.data, interactionEntries(interaction))
      this.data['cmi.entry'] = initialData.suspendData || initialData.location ? 'resume' : 'ab-initio'
    } else {
      this.data['cmi.entry'] = 'ab-initio'
    }

    this.data['cmi.completion_status'] = this.data['cmi.completion_status'] || 'not attempted'
    this.data['cmi.success_status'] = this.data['cmi.success_status'] || 'unknown'
    this.data['cmi.exit'] = ''
    this.data['cmi.mode'] = 'normal'
    this.data['cmi.credit'] = 'credit'
  }

  Initialize(_param: string = ''): string {
    if (this.initialized) {
      this.lastError = ErrorCode.ALREADY_INITIALIZED
      return 'false'
    }
    if (this.terminated) {
      this.lastError = ErrorCode.CONTENT_INSTANCE_TERMINATED
      return 'false'
    }
    this.initialized = true
    this.lastError = ErrorCode.NO_ERROR
    return 'true'
  }

  Terminate(_param: string = ''): string {
    if (!this.initialized) {
      this.lastError = ErrorCode.TERMINATE_BEFORE_INIT
      return 'false'
    }
    if (this.terminated) {
      this.lastError = ErrorCode.TERMINATE_AFTER_TERMINATE
      return 'false'
    }

    this.Commit()
    // Fin de session : rien n'attend plus, la page peut disparaître juste après.
    this.sendPendingNow()
    this.terminated = true
    this.initialized = false
    this.lastError = ErrorCode.NO_ERROR
    this.onTerminate?.({
      completed: this.data['cmi.completion_status'] === 'completed'
        || this.data['cmi.success_status'] === 'passed',
    })
    return 'true'
  }

  GetValue(key: string): string {
    if (!this.initialized) {
      this.lastError = ErrorCode.GET_BEFORE_INIT
      return ''
    }
    if (this.terminated) {
      this.lastError = ErrorCode.GET_AFTER_TERMINATE
      return ''
    }

    this.lastError = ErrorCode.NO_ERROR

    if (key === 'cmi._version') return '1.0'
    if (key === 'cmi.learner_name') return ''
    if (key === 'cmi.learner_id') return ''

    if (key.endsWith('._count')) {
      const prefix = key.replace('._count', '')
      let count = 0
      for (const k of Object.keys(this.data)) {
        const match = k.match(new RegExp(`^${prefix.replace(/\./g, '\\.')}\\.(\\d+)\\.`))
        if (match) {
          const idx = Number.parseInt(match[1], 10) + 1
          if (idx > count) count = idx
        }
      }
      return String(count)
    }

    return this.data[key] ?? ''
  }

  SetValue(key: string, value: string): string {
    if (!this.initialized) {
      this.lastError = ErrorCode.SET_BEFORE_INIT
      return 'false'
    }
    if (this.terminated) {
      this.lastError = ErrorCode.SET_AFTER_TERMINATE
      return 'false'
    }

    const readOnlyKeys = ['cmi._version', 'cmi.learner_id', 'cmi.learner_name', 'cmi.entry', 'cmi.mode', 'cmi.credit']
    if (readOnlyKeys.includes(key)) {
      this.lastError = ErrorCode.DATA_MODEL_ELEMENT_IS_READ_ONLY
      return 'false'
    }

    this.data[key] = value
    if (key === 'cmi.success_status') this.successSetByContent = true
    const interaction = interactionIndexOf(key)
    if (interaction !== null) this.changedInteractions.add(interaction)
    this.lastError = ErrorCode.NO_ERROR
    return 'true'
  }

  Commit(_param: string = ''): string {
    if (!this.initialized) {
      this.lastError = ErrorCode.COMMIT_BEFORE_INIT
      return 'false'
    }
    if (this.terminated) {
      this.lastError = ErrorCode.COMMIT_AFTER_TERMINATE
      return 'false'
    }

    // Le statut de réussite n'est transmis que si le contenu l'a posé lui-même
    // (SetValue direct, sinon rollup de ses objectifs). Sans signal, le champ
    // est omis et l'API dérive PASSED/FAILED du score et du seuil du manifeste.
    const scormData = commitPayload(this.data, {
      initialTotalTimeMs: this.initialTotalTimeMs,
      successSetByContent: this.successSetByContent,
    })
    // Le delta d'interactions est lu à l'ENVOI, pas ici : un commit en file
    // emporte ainsi aussi ce qu'une sauvegarde précédente n'a pas pu livrer.
    const send = () => {
      const sent = this.takeChangedInteractions()
      return this.onCommit(sent.length > 0 ? { ...scormData, interactions: sent } : scormData)
        .catch((error: unknown) => {
          // Sauvegarde perdue : ses interactions repartiront avec la suivante.
          for (const { index } of sent) this.changedInteractions.add(index)
          console.error(error)
        })
    }
    // onCommit est appelé tout de suite quand rien n'est en vol, sinon après le
    // commit précédent. La promise est suivie pour que flushCommits() l'attende.
    const commitPromise: Promise<void> = this.schedule(send).finally(() => {
      this.commitQueue = this.commitQueue.filter(p => p !== commitPromise)
    })
    this.commitQueue.push(commitPromise)
    this.lastError = ErrorCode.NO_ERROR
    return 'true'
  }

  /**
   * Attend la résolution de tous les commits en vol (les erreurs sont déjà
   * absorbées par la queue, la promise renvoyée ne rejette jamais).
   */
  async flushCommits(): Promise<void> {
    while (this.commitQueue.length > 0) {
      await Promise.all(this.commitQueue)
    }
  }

  private schedule(send: () => Promise<void>): Promise<void> {
    return new Promise((resolve) => {
      let started = false
      const run = () => {
        if (started) return
        started = true
        this.sending = true
        void send().finally(() => {
          resolve()
          this.sendNextCommit()
        })
      }
      if (this.sending) this.waitingCommits.push(run)
      else run()
    })
  }

  private sendNextCommit(): void {
    const next = this.waitingCommits.shift()
    if (next) next()
    else this.sending = false
  }

  /**
   * Envoie sans attendre les commits en file : à la fin de la session ou quand
   * la page se ferme, attendre le commit en vol, c'est ne jamais envoyer les
   * suivants. Une course côté serveur est alors absorbée par l'API (rejeu P2002).
   */
  sendPendingNow(): void {
    for (const run of this.waitingCommits.splice(0)) run()
  }

  /** Interactions modifiées depuis le dernier envoi, au format d'envoi ; le delta est remis à zéro. */
  private takeChangedInteractions(): ScormInteractionData[] {
    const indices = [...this.changedInteractions].sort((a, b) => a - b)
    this.changedInteractions.clear()
    return indices
      .map(index => readInteraction(this.data, index))
      .filter(interaction => interaction !== null)
      .map(this.formatInteraction)
  }

  /** Traduction appliquée aux interactions avant l'envoi (l'adapter 1.2 y passe ses réponses). */
  setInteractionFormat(format: (interaction: ScormInteractionData) => ScormInteractionData): void {
    this.formatInteraction = format
  }

  /** État exposé pour les adapters (ex. SCORM 1.2) qui valident avant de déléguer. */
  isInitialized(): boolean {
    return this.initialized
  }

  /** Dernier code d'erreur brut (2004), pour la traduction côté adapter 1.2. */
  getLastErrorCode(): ErrorCode {
    return this.lastError
  }

  /**
   * Réinitialise l'axe success à « non posé par le contenu » : l'adapter 1.2
   * l'appelle quand le lesson_status passe à une valeur sans signal passed/failed
   * (completed/incomplete/browsed/not attempted). Sans cela, un `failed`
   * précédent resterait figé et serait réémis à chaque commit. Une fois remis à
   * plat, Commit omet successStatus et l'API le dérive du score (cf. bridge 2004).
   */
  resetSuccessStatus(): void {
    this.data['cmi.success_status'] = 'unknown'
    this.successSetByContent = false
  }

  GetLastError(): string {
    return String(this.lastError)
  }

  GetErrorString(errorCode: string): string {
    const code = Number.parseInt(errorCode, 10)
    return SCORM2004_ERROR_STRINGS[code] || 'Unknown Error'
  }

  GetDiagnostic(errorCode: string): string {
    return this.GetErrorString(errorCode)
  }
}
